// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useEffect, useMemo, useState } from 'react';
import { Api } from '../../../../../services/config/Api';
import { useDatasets } from '../../../../../state/hooks/DatasetHooks';
import { useOrganizationId } from '../../../../../state/hooks/OrganizationHooks';
import {
  useCurrentScenarioId,
  useCurrentScenarioParametersValues,
  useCurrentScenarioDatasetList,
} from '../../../../../state/hooks/ScenarioHooks';
import { useWorkspaceId } from '../../../../../state/hooks/WorkspaceHooks';
import {
  useCriteresDeclenchementByGisement,
  useFamilleEquipementByAssetClass,
  useNiveauDePriorisation,
  useReglesGestionByGisement,
  useSequenceActionByGisement,
  useTableNamesMappingByGisement,
  useTwingraphQueriesStatus,
} from '../../../../../state/wizard/WizardHooks';
import { WizardUtils } from '../../../../../utils/WizardUtils';

export const useReglesGestion = (selectedGisement) => {
  const scenarioId = useCurrentScenarioId();
  const reglesGestion = useReglesGestionByGisement(selectedGisement, scenarioId);
  const criteresDeclenchement = useCriteresDeclenchementByGisement(selectedGisement, scenarioId);
  const status = useTwingraphQueriesStatus();
  const niveauDePriorisation = useNiveauDePriorisation(scenarioId)
    .map((np) => np.Identifiant)
    .filter((id) => id)
    .sort();
  const tableNamesMapping = useTableNamesMappingByGisement(scenarioId, selectedGisement);
  const sequencesActions = useSequenceActionByGisement(selectedGisement, scenarioId);
  const sequenceActionsOptions = useMemo(() => {
    const sequencesAction = Array.from(new Set(sequencesActions?.map((sa) => sa.SequenceActions)));
    return WizardUtils.sortResourceListAlphabetically(sequencesAction);
  }, [sequencesActions]);

  const organizationId = useOrganizationId();
  const workspaceId = useWorkspaceId();
  const datasets = useDatasets();
  const currentScenarioParametersValues = useCurrentScenarioParametersValues();
  const assetClass = tableNamesMapping?.ClasseActif;
  const familleEquipement = useFamilleEquipementByAssetClass(scenarioId, assetClass);
  const datasetId = useCurrentScenarioDatasetList()[0];

  const [enveloppesBudgetaire, setEnveloppesBudgetaire] = useState([]);

  useEffect(() => {
    const fetchEnveloppesBudgetaire = async () => {
      const enveloppesBudgetairesParameterValue = currentScenarioParametersValues.find(
        (parameter) => parameter.parameterId === 'EnveloppesBudgetaires'
      )?.value;
      if (enveloppesBudgetairesParameterValue == null || enveloppesBudgetairesParameterValue === '') {
        const { data } = await Api.Datasets.twingraphQuery(organizationId, datasetId, {
          query: 'MATCH (n: EnveloppesBudgetaires) RETURN n',
        });
        setEnveloppesBudgetaire(data.map((node) => node.n.properties) ?? []);
      } else {
        try {
          const data = await WizardUtils.downloadFileData(
            organizationId,
            workspaceId,
            datasets,
            enveloppesBudgetairesParameterValue
          );
          setEnveloppesBudgetaire(data);
        } catch (error) {
          console.error('Error downloading file data for EnveloppesBudgetaires:', error);
          setEnveloppesBudgetaire([]);
        }
      }
    };

    fetchEnveloppesBudgetaire();
  }, [organizationId, workspaceId, datasets, datasetId, currentScenarioParametersValues]);

  const categoriesBudgetaires = useMemo(() => {
    const categories = [
      ...new Set((enveloppesBudgetaire || []).map((enveloppes) => enveloppes.CategoriesBudgetaires.split('|')).flat()),
    ];
    return WizardUtils.sortResourceListAlphabetically(categories);
  }, [enveloppesBudgetaire]);

  const [referentielConsequences, setReferentielConsequences] = useState([]);

  useEffect(() => {
    const fetchReferentielConsequences = async () => {
      const { data } = await Api.Datasets.twingraphQuery(organizationId, datasetId, {
        query: 'MATCH (n: ReferentielConsequences) WHERE n.EvenementDeclencheur = "EquipmentComponentFailure" RETURN n',
      });
      setReferentielConsequences(data.map((node) => node.n.properties) ?? []);
    };

    fetchReferentielConsequences();
  }, [organizationId, datasetId]);

  return {
    reglesGestion,
    status,
    criteresDeclenchement,
    niveauDePriorisation,
    tableNamesMapping,
    familleEquipement,
    sequenceActionsOptions,
    enveloppesBudgetaire,
    categoriesBudgetaires,
    referentielConsequences,
  };
};
